import * as React from 'react';
import { format, isValid, parse } from 'date-fns';
import { it } from 'date-fns/locale';
import * as Popover from '@radix-ui/react-popover';
import type { DateRange } from 'react-day-picker';
import { Calendar as CalendarIcon, CalendarRange, X } from 'lucide-react';

import { cn } from '../../lib/utils';
import { usePortalContainer } from '@/lib/portalContainerContext';
import { Calendar } from './calendar';
import { Input } from './input';

const DISPLAY_FORMAT = 'dd/MM/yyyy' as const;

function parseInputToDate(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const isoDate = new Date(trimmed);
    return isValid(isoDate) ? isoDate : undefined;
  }

  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    return undefined;
  }

  const parsed = parse(trimmed, DISPLAY_FORMAT, new Date());
  if (isValid(parsed)) return parsed;

  return undefined;
}

interface DatePickerProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'defaultValue' | 'onSelect' | 'size'
> {
  date?: Date;
  onSelect?: (date: Date | undefined) => void;
  onTextChange?: (raw: string) => void;
  placeholder?: string;
  isClearable?: boolean;
  className?: string;
  inputClassName?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function DatePicker({
  date,
  onSelect,
  onTextChange,
  placeholder = 'Seleziona data',
  disabled = false,
  isClearable = true,
  className,
  inputClassName,
  id,
  name,
  size = 'md',
  'aria-label': ariaLabel,
  ...inputProps
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(
    date ? format(date, DISPLAY_FORMAT, { locale: it }) : ''
  );
  const { onMouseDown: inputMouseDown, ...restInputProps } = inputProps;
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Get portal container from context (provided by Dialog/Drawer)
  const portalContainer = usePortalContainer();

  const activeDate = React.useMemo(() => {
    return parseInputToDate(inputValue) ?? date;
  }, [date, inputValue]);

  React.useEffect(() => {
    setInputValue(date ? format(date, DISPLAY_FORMAT, { locale: it }) : '');
  }, [date]);

  const handleSelect = (selected?: Date) => {
    if (!selected) {
      setInputValue('');
      onTextChange?.('');
      onSelect?.(undefined);
      setOpen(false);
      return;
    }

    const formatted = format(selected, DISPLAY_FORMAT, { locale: it });
    setInputValue(formatted);
    onTextChange?.(formatted);
    onSelect?.(selected);
    setOpen(false);
    // keep focus on the text input for quick navigation
    inputRef.current?.focus();
  };

  const formatDateInputValue = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const handleInputChange = (value: string) => {
    const nextValue = formatDateInputValue(value);
    setInputValue(nextValue);
    onTextChange?.(nextValue);

    if (nextValue.trim() === '') {
      onSelect?.(undefined);
      return;
    }

    const digits = nextValue.replace(/\D/g, '');
    if (digits.length === 8 && /^\d{2}\/\d{2}\/\d{4}$/.test(nextValue)) {
      const parsed = parseInputToDate(nextValue);
      if (parsed) {
        onSelect?.(parsed);
      }
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter') {
      event.preventDefault();
      setOpen(true);
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div ref={anchorRef} className={cn('relative w-full', className)}>
          <Input
            id={id}
            name={name}
            ref={inputRef}
            value={inputValue}
            placeholder={placeholder}
            onChange={(event) => handleInputChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onMouseDown={(event) => {
              if (disabled) return;
              inputMouseDown?.(event);
              if (!event.defaultPrevented) {
                setOpen(true);
              }
            }}
            inputMode="numeric"
            pattern="\\d{2}/\\d{2}/\\d{4}"
            size={size}
            autoComplete="off"
            aria-label={ariaLabel ?? placeholder}
            disabled={disabled}
            className={inputClassName}
            {...restInputProps}
          />

          {isClearable && !!inputValue && !disabled && (
            <button
              type="button"
              tabIndex={-1}
              className="absolute right-11 top-1/2 -translate-y-1/2 rounded-full p-1 text-text-tertiary transition hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30 flex items-center justify-center"
              onClick={() => handleSelect(undefined)}
              aria-label="Pulisci data"
              title="Pulisci data"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          <Popover.Trigger asChild>
            <button
              type="button"
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary transition-colors hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30 rounded-full p-1 flex items-center justify-center"
              aria-label="Apri calendario"
              title="Apri calendario"
              disabled={disabled}
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </Popover.Trigger>
        </div>
      </Popover.Anchor>

      <Popover.Portal container={portalContainer ?? undefined}>
        <Popover.Content
          className="z-50 mt-2 w-auto rounded-lg border border-[var(--border-default)] bg-surface p-0 shadow-xl"
          align="start"
          sideOffset={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            const target = event.target as Node | null;
            if (target && anchorRef.current?.contains(target)) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            const target = event.target as Node | null;
            if (target && anchorRef.current?.contains(target)) {
              event.preventDefault();
            }
          }}
        >
          <Calendar
            mode="single"
            selected={activeDate}
            defaultMonth={activeDate}
            onSelect={handleSelect}
            locale={it}
            captionLayout="dropdown"
            fromYear={1950}
            toYear={new Date().getFullYear() + 10}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

interface DateRangePickerProps {
  dateRange?: DateRange;
  onSelect?: (range: DateRange | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function DateRangePicker({
  dateRange,
  onSelect,
  placeholder = 'Seleziona intervallo',
  disabled = false,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  // Get portal container from context (provided by Dialog/Drawer)
  const portalContainer = usePortalContainer();

  const formatRange = (range?: { from?: Date; to?: Date }) => {
    if (!range?.from) return placeholder;
    if (!range.to) return format(range.from, DISPLAY_FORMAT, { locale: it });
    return `${format(range.from, DISPLAY_FORMAT, { locale: it })} - ${format(range.to, DISPLAY_FORMAT, { locale: it })}`;
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div ref={anchorRef} className="w-full">
          <Popover.Trigger asChild>
            <button
              type="button"
              className={cn(
                'flex h-10 w-full items-center justify-start gap-2 rounded-md border border-[var(--border-default)] bg-surface px-3 text-left text-sm font-normal text-text-primary shadow-sm transition-all duration-150 hover:border-[var(--input-border-hover)] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500/30',
                !dateRange?.from && 'text-text-tertiary',
                className
              )}
              disabled={disabled}
              aria-label="Apri intervallo date"
              title="Apri intervallo date"
            >
              <CalendarRange className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{formatRange(dateRange)}</span>
            </button>
          </Popover.Trigger>
        </div>
      </Popover.Anchor>
      <Popover.Portal container={portalContainer ?? undefined}>
        <Popover.Content
          className="z-50 mt-2 w-auto rounded-lg border border-[var(--border-default)] bg-surface p-0 shadow-xl"
          align="start"
          sideOffset={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            const target = event.target as Node | null;
            if (target && anchorRef.current?.contains(target)) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            const target = event.target as Node | null;
            if (target && anchorRef.current?.contains(target)) {
              event.preventDefault();
            }
          }}
        >
          <Calendar
            mode="range"
            selected={dateRange}
            defaultMonth={dateRange?.from}
            onSelect={onSelect}
            numberOfMonths={2}
            locale={it}
            captionLayout="dropdown"
            fromYear={1950}
            toYear={new Date().getFullYear() + 10}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
